import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Copy, RefreshCw, Trash2, Upload, ImageOff } from 'lucide-react';
import api from '../../services/api';

/**
 * Banco de imagens das landing pages.
 *
 * O mesmo acervo que aparece dentro do editor, agora acessível sem precisar abrir
 * (ou criar) uma página: o cliente reclamava de "abrir uma LP em branco só para subir
 * a imagem e pegar o link" — e acabava com centenas de cópias iguais no S3.
 */

type Imagem = { url: string; key: string; name: string; size?: number; lastModified?: string };

export default function ImageLibraryPanel() {
  const [imagens, setImagens] = useState<Imagem[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const [busca, setBusca] = useState('');
  const [enviando, setEnviando] = useState(false);
  const inputArquivo = useRef<HTMLInputElement>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(false);
    try {
      const res = await api.get('/upload/images', { params: { limit: 200 } });
      if (res.data?.success) {
        setImagens(res.data.data || []);
        setTotal(typeof res.data.total === 'number' ? res.data.total : (res.data.data || []).length);
      } else {
        setErro(true);
      }
    } catch (e) {
      // Falha de rede não pode parecer "você não tem imagens", senão o cliente sobe tudo de novo
      console.error('[banco-de-imagens] falha ao listar:', e);
      setErro(true);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const copiarLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copiado');
    } catch {
      // Navegador sem permissão de área de transferência (http, iframe): mostra para copiar na mão
      window.prompt('Copie o link da imagem:', url);
    }
  };

  const enviar = async (arquivo: File) => {
    setEnviando(true);
    try {
      const form = new FormData();
      form.append('file', arquivo);
      const res = await api.post('/upload/image', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data?.url || res.data?.data?.url;
      if (url) {
        toast.success('Imagem enviada');
        await carregar();
        copiarLink(url);
      } else {
        toast.error('Não consegui enviar a imagem');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Não consegui enviar a imagem');
    } finally {
      setEnviando(false);
      if (inputArquivo.current) inputArquivo.current.value = '';
    }
  };

  const excluir = async (img: Imagem) => {
    if (!window.confirm(`Excluir "${img.name}"? Páginas que usam essa imagem ficam sem ela.`)) return;
    try {
      await api.delete('/upload/images', { params: { key: img.key } });
      setImagens((atual) => atual.filter((i) => i.key !== img.key));
      setTotal((t) => Math.max(0, t - 1));
      toast.success('Imagem excluída');
    } catch {
      toast.error('Não consegui excluir a imagem');
    }
  };

  const filtradas = busca.trim()
    ? imagens.filter((i) => (i.name || '').toLowerCase().includes(busca.trim().toLowerCase()))
    : imagens;

  return (
    <div className="space-y-4" data-testid="image-library-panel">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Banco de imagens {total > 0 && <span className="text-gray-500 font-normal">({total})</span>}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Tudo que você já enviou fica aqui. Copie o link e use em qualquer página, e-mail ou anúncio.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={inputArquivo}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) enviar(f); }}
          />
          <button
            onClick={() => inputArquivo.current?.click()}
            disabled={enviando}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            {enviando ? 'Enviando...' : 'Enviar imagem'}
          </button>
          <button
            onClick={carregar}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm flex items-center gap-2 text-gray-700 dark:text-gray-300"
          >
            <RefreshCw className={`w-4 h-4 ${carregando ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      <input
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        placeholder="Buscar pelo nome do arquivo..."
        className="w-full sm:max-w-sm px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
      />

      {erro ? (
        <div className="p-6 text-center border border-dashed border-red-300 rounded-xl text-sm text-red-600">
          Não consegui carregar suas imagens agora. Clique em Atualizar para tentar de novo.
        </div>
      ) : carregando && imagens.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-500">Carregando...</div>
      ) : filtradas.length === 0 ? (
        <div className="p-10 text-center border border-dashed border-gray-300 dark:border-gray-700 rounded-xl">
          <ImageOff className="w-8 h-8 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">
            {busca ? 'Nenhuma imagem com esse nome.' : 'Você ainda não enviou imagens.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {filtradas.map((img) => (
            <div key={img.key} className="group border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800">
              <div className="aspect-square bg-gray-100 dark:bg-gray-900 flex items-center justify-center overflow-hidden">
                <img src={img.url} alt={img.name} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-2 space-y-2">
                <p className="text-xs text-gray-600 dark:text-gray-300 truncate" title={img.name}>{img.name}</p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => copiarLink(img.url)}
                    className="flex-1 px-2 py-1.5 text-xs rounded-lg bg-primary-600 hover:bg-primary-700 text-white flex items-center justify-center gap-1"
                  >
                    <Copy className="w-3 h-3" /> Copiar link
                  </button>
                  <button
                    onClick={() => excluir(img)}
                    className="px-2 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    title="Excluir imagem"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {total > imagens.length && (
        <p className="text-xs text-gray-500">
          Mostrando as {imagens.length} mais recentes de {total}. As demais continuam salvas.
        </p>
      )}
    </div>
  );
}
